import { BooleanObject } from '@/models/GenericInterfaces'
import { Culture } from '@/models/Global'
import { ProductInput } from '@/models/Import'
import { SimplifiedProduct } from '@/models/Product'
import { AdvancedProperty, EditableAdvancedProperty, PropertyUsingComponent, SelectedPropertyRecord } from '@/models/Property'
import PropertyService from '@/services/PropertyService'
import { usePropertyStore } from '@/store/propertyStore'
import { countryDoesNotExclude } from '@/utils/productUtils'
import { arrayToRecord, search, toComparable, toSafeString } from '@/utils/tools'
import { computed, ref, Ref } from 'vue'

export const useSelectedProperties = (
  usingComponent: PropertyUsingComponent,
  activeProduct?: Ref<SimplifiedProduct | undefined>,
  productInput?: Ref<ProductInput | undefined>,
) => {
  const propertyStore = usePropertyStore()

  // ----------
  // -- Refs --
  // ----------
  const selectedPropertyKeys = ref<BooleanObject>({})
  const selectedDeadlines = ref<SelectedPropertyRecord>({})
  const selectedCultures = ref<Culture[]>([])
  const propertySearchTerm = ref<string>('')
  const propertyGroups = ref<EditableAdvancedProperty[][]>()

  // --------------------
  // -- Computed props --
  // --------------------
  const properties = computed(() => propertyGroups.value?.flat())

  const propertiesByComparable = computed(() => arrayToRecord(properties.value || [], 'defaultHeader', toComparable))

  const nonDefaultCulturalProperties = computed(() => properties.value?.filter(isNonDefaultCulturalProperty))

  const isNonDefaultCulturalProperty = (property: AdvancedProperty) =>
    (property.type !== 'languageSpecific' && property.type !== 'countrySpecific') || !!property.culturePrefix // filter out "default" cultural properties (properties without culturePrefix) for products bulk

  /** Selected properties, filtering out cultural properties without culture prefix  */
  const selectedProperties = computed(() => {
    return (
      nonDefaultCulturalProperties.value?.filter((property) => {
        if (!propertyIsIncludedDueToDeadline(property)) {
          return false
        }

        if (selectedPropertyKeys.value[property.key] && !property.sticky) {
          if (property.culturePrefix) {
            return propertyCultureMatches(property)
          } else if (property.exclusiveToCountries) {
            const countries = new Set(property.exclusiveToCountries)
            return selectedCultures.value?.some((culture) => countries.has(culture.country))
          }

          return true
        }
      }) || []
    )
  })

  /**
   * Filters out properties based on selected properties, culture, and search term
   * Used by single products which does not loop over these
   */
  const visiblePropertyKeys = computed(() => {
    return propertyGroups.value?.reduce((acc, group) => {
      for (const property of group) {
        if (!isNonDefaultCulturalProperty(property)) {
          continue
        }

        if (!propertyIsIncludedDueToDeadline(property)) {
          continue
        }

        const culture = propertyStore.getPropertyCulture(property)

        if (!propertyCultureMatches(property)) {
          continue
        }

        if (selectedPropertyKeys.value[property.key]) {
          const propertyExistingResult = property.get(activeProduct?.value, undefined)

          const propertyEditedResult = property.get(undefined, productInput?.value)

          const propertyExistingOptions = propertyExistingResult?.options?.join(' ')
          const propertyEditedOptions = propertyEditedResult?.options?.join(' ')

          const propertyExistingDefaultOptions = propertyExistingResult?.defaultOptions?.join(' ')
          const propertyEditedDefaultOptions = propertyEditedResult?.defaultOptions?.join(' ')

          const propertyIsNotExcludedByCulture = !culture || countryDoesNotExclude(property.exclusiveToCountries, culture.country)

          const matchesSearchTerm = () =>
            search(toSafeString(property.defaultName), propertySearchTerm.value) ||
            search(toSafeString(property.header), propertySearchTerm.value) ||
            (propertyExistingOptions && search(propertyExistingOptions, propertySearchTerm.value)) ||
            (propertyEditedOptions && search(propertyEditedOptions, propertySearchTerm.value)) ||
            (propertyExistingDefaultOptions && search(propertyExistingDefaultOptions, propertySearchTerm.value)) ||
            (propertyEditedDefaultOptions && search(propertyEditedDefaultOptions, propertySearchTerm.value))

          const matches = !!(
            propertyIsNotExcludedByCulture &&
            (property.type !== 'countrySpecific' || culture?.country.toLowerCase() === property.culturePrefix?.toLowerCase()) &&
            (property.type !== 'languageSpecific' || culture?.language.toLowerCase() === property.culturePrefix?.toLowerCase()) &&
            (!propertySearchTerm.value || matchesSearchTerm())
          )

          if (matches) {
            acc.add(property.defaultHeader)
          }
        }
      }

      return acc
    }, new Set<string>())
  })

  const propertyKeysIncludedInSelectedDeadlines = computed(() =>
    properties.value?.reduce((acc, property) => {
      const propertyDeadlineLevel = (property.id && propertyStore.deadlinesById?.[property.id]?.level) ?? propertyStore.deadlinesByKey?.[property.key]?.level
      if (propertyDeadlineLevel ? selectedDeadlines.value[propertyDeadlineLevel] : selectedDeadlines.value.untagged) {
        acc.add(property.key)
      }

      return acc
    }, new Set<string>()),
  )

  // ---------------
  // -- Functions --
  // ---------------
  const initProperties = async (forceUpdate?: boolean) => {
    if (!propertyGroups.value || forceUpdate) {
      propertyGroups.value = await PropertyService.getPropertyGroups(usingComponent)
    }
  }

  const propertyShouldBeShown = (property: AdvancedProperty) => visiblePropertyKeys.value?.has(property.defaultHeader) // use to hide rows rather than prevent rendering

  const propertyCultureMatches = (property: AdvancedProperty) => {
    if (property.culturePrefix) {
      const cultureType = property.type === 'languageSpecific' ? 'language' : 'country'
      return selectedCultures.value?.some((culture) => culture[cultureType] === property.culturePrefix)
    }
    return true
  }

  const getPropertyByName = (name: string, culturePrefix?: string) => {
    if (!properties.value) {
      console.log('getPropertyByName: properties not initiated')
      return
    }

    const key = PropertyService.getHeader(name, culturePrefix).toLowerCase()
    const property = propertiesByComparable.value[key]
    if (property) {
      return property
    } else {
      console.log('getPropertyByName: No property matching', key)
    }
  }

  const propertyIsIncludedDueToDeadline = (property: AdvancedProperty) => {
    const propertyDeadlineLevel = (property.id && propertyStore.deadlinesById?.[property.id]?.level) ?? propertyStore.deadlinesByKey?.[property.key]?.level
    return propertyDeadlineLevel ? selectedDeadlines.value[propertyDeadlineLevel] : selectedDeadlines.value.untagged
  }

  return {
    properties,
    nonDefaultCulturalProperties,
    propertyGroups,
    selectedCultures,
    propertySearchTerm,
    selectedProperties,
    selectedPropertyKeys,
    selectedDeadlines,
    propertyKeysIncludedInSelectedDeadlines,
    propertyShouldBeShown,
    initProperties,
    getPropertyByName,
  }
}
